<template>
	<div class="giphy-drawer" @keyup.esc="close">
		<div class="giphy-container">
			<div class="giphy-heading">
				<div class="powered-by-giphy" />
				<CloseIcon @click="close" hover />
			</div>

			<div class="content">
				<Input
					class="txt-giphy-search"
					v-model="searchQuery"
					allowClear
					autoFocus
					:placeholder="$t('chat_gif_search_placeholder')"
				/>

				<div class="grid-container">
					<div
						class="no-results"
						v-if="hasNoResults && !isLoading"
						v-t="'chat_gif_search_noresults'"
					/>
					<div class="no-results" v-if="isLoading">
						<Spinner />
						<div
							v-text="
								searchQuery
									? $t('search_loading')
									: $t('common_loader_loading_title')
							"
						/>
					</div>
					<div
						ref="grid"
						class="grid ph-no-capture"
						:class="{ show: showResults }"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { getOrganization } from '../store'
import Input from '@/components/common/Input'
import Spinner from '@/components/common/Spinner'
import { GiphyFetch } from '@giphy/js-fetch-api'
import { renderGrid } from '@giphy/js-components'
import { CloseIcon } from '@/icons'
import debounce from 'debounce'
import i18n from '@/i18n'

export default {
	components: { Spinner, CloseIcon, Input },
	data() {
		return {
			searchQuery: '',
			isLoading: true,
			hasNoResults: false,
		}
	},

	emits: ['close', 'click'],

	created() {
		this.giphy = new GiphyFetch('26hiszLVSnMFbhh96')
		this.buildGridDebounced = debounce(this.buildGrid, 500)
	},

	mounted() {
		this.buildGrid()
	},

	computed: {
		showResults() {
			return !this.hasNoResults && !this.isLoading
		},
	},

	watch: {
		async searchQuery() {
			this.isLoading = true
			this.removeGrid()
			this.showNoResults = false
			await this.$nextTick()
			this.buildGridDebounced()
		},
	},

	methods: {
		close() {
			this.$emit('close')
		},

		async fetchGifs(offset) {
			this.isLoading = offset === 0 // only show loading state if fetching first page of results
			const org = getOrganization()
			const options = {
				offset,
				limit: 10,
				rating: org.giphy_rating,
				lang: i18n.global.locale,
			}
			let resp = null
			if (this.searchQuery) {
				resp = await this.giphy.search(this.searchQuery, {
					...options,
					sort: 'relevant',
				})
			} else {
				resp = await this.giphy.trending(options)
			}
			this.hasNoResults = resp.data?.length === 0 ?? false
			this.isLoading = false
			return resp
		},

		buildGrid() {
			this.removeGrid = renderGrid(
				{
					width: 420,
					columns: 2,
					gutter: 8,
					noLink: true,
					hideAttribution: true,
					noResultsMessage: null,
					fetchGifs: this.fetchGifs.bind(this),
					onGifClick: this.click.bind(this),
				},
				this.$refs.grid
			)
		},

		click(gif) {
			this.$emit('click', gif)
			this.close()
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

.close-giphy-drawer {
	top: 14px;
	right: 20px;
	position: absolute;
	outline: 0;
}

.giphy-drawer {
	background-color: var(--neutral-200);
	border-radius: 4px;
	right: -8px;
	bottom: 42px;
	height: 560px;
	max-height: calc(100vh - 170px) !important;
	width: 450px;
	position: absolute;
	font-family: Avenir-Medium;
	font-size: 16px;
	color: #4f5053;
	@include max-z;
	box-shadow: rgba(0, 0, 0, 0.3) 0 4px 6px 2px;
	&::before {
		content: '';
		position: absolute;
		box-shadow: rgba(0, 0, 0, 0.3) 3px 3px 3px -2px;
		transform: rotate(45deg);
		bottom: -10px;
		right: 11px;
		border-width: 11px;
		border-style: solid;
		border-color: transparent var(--neutral-200) var(--neutral-200) transparent;
	}
	@media screen and (max-width: 800px) {
		max-height: calc(100vh - 130px) !important;
		.grid-container {
			max-height: calc(100vh - 266px) !important;
		}
	}
}

.content {
	position: relative;
	padding: 8px;
}

.grid-container {
	height: 420px;
	max-height: calc(100vh - 304px) !important;
	overflow-x: hidden;
	overflow-y: auto;
	.grid {
		opacity: 0;
		transition: opacity 150ms linear;
		&.show {
			opacity: 1;
		}
		:deep(.giphy-gif-img) {
			// https://css-tricks.com/a-complete-guide-to-dark-mode-on-the-web/#aa-dark-mode-images
			filter: brightness(0.8) contrast(1.2);
		}
	}
	.no-results {
		@include flex-column-center(8px);
		width: 100%;
		text-align: center;
		padding: 80px 0;
		.pronto-spinner {
			font-size: 30px;
		}
	}
}

.giphy-container {
	position: relative;
	height: 100%;
}

.txt-giphy-search {
	width: 100%;
	margin-bottom: 8px;
}

.powered-by-giphy {
	width: 100px;
	height: 11px;
	-o-object-fit: contain;
	object-fit: contain;
	background-image: url('/images/common/poweredbygiphy.png');
	background-repeat: no-repeat;
	display: inline-block;
	border: none;
	margin-left: 9px;
	background-size: contain;
}

.giphy-heading {
	@include flex-spread;
	width: 100%;
	height: 55px;
	padding: 0 16px;
	color: var(--neutral-800);
	border-bottom: 1px solid var(--neutral-40040);
	.pronto-icon {
		font-size: 24px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.giphy-drawer {
		.txt-giphy-search {
			background: var(--neutral-100);
		}
		&.focused {
			border-color: var(--teal-300);
		}
	}
	.powered-by-giphy {
		background-image: url('/images/common/poweredbygiphy_dark.png');
	}
}
</style>
